import { Box, CircularProgress } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { practiceProfileApi } from "apis";
import AddBranchModal from "components/features/practice/AddBranchModal";
import DeleteBranchModal from "components/features/practice/DeleteBranchModal";
import PracticeBranchesView from "components/features/practice/PracticeBranchesView";
import PracticeViewDetails from "components/features/practice/PracticeViewDetails";
import StaffMembersModal from "components/features/practice/StaffMembersModal";
import UpdateBranchModal from "components/features/practice/UpdateBranchModal";
import UpdateLogoModal from "components/features/practice/UpdateLogoModal";
import UpdateNameModal from "components/features/practice/UpdateNameModal";
import DashboardLayout from "components/layouts/DashboardLayout";
import { EPath } from "constants/path";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { FirebaseStorageService } from "helpers/firebaseStorage";
import type {
  PracticeProfileViewDto,
  BranchDto,
  CreatePracticeBranchDto,
  UpdatePracticeBranchDto,
} from "libs/cellera-sdk";
import PatientDetailPageContainer from "pages/components/PatientDetailPageContainer";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

const EMPTY_PROFILE: Omit<PracticeProfileViewDto, "id" | "email"> = {
  profilePicture: "",
  practiceName: "",
  phoneNumber: "",
  branches: [],
};

function PracticeProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // Modal states
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [addBranchModalOpen, setAddBranchModalOpen] = useState(false);
  const [updateBranchModalOpen, setUpdateBranchModalOpen] = useState(false);
  const [deleteBranchModalOpen, setDeleteBranchModalOpen] = useState(false);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<BranchDto | null>(null);
  const [selectedBranchForStaff, setSelectedBranchForStaff] =
    useState<BranchDto | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["practice-profile-view", user?.uid],
    queryFn: async () => {
      return await practiceProfileApi.getPracticeProfileView();
    },
    enabled: Boolean(user?.uid),
    retry: false,
  });

  const handleBack = useCallback(() => {
    navigate(EPath.PROVIDER_DASHBOARD);
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Memoize profile with fallback values
  const profile = useMemo(() => {
    return (
      data || {
        ...EMPTY_PROFILE,
        id: user?.uid || "",
        email: user?.email || "",
      }
    );
  }, [data, user?.uid, user?.email]);

  // ========== Mutations ==========

  const updateLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const logoUrl = await FirebaseStorageService.uploadPracticeLogo(
        file,
        profile.practiceId || profile.id
      );
      await practiceProfileApi.practiceControllerUpdatePractice({
        updatePracticeDto: { logoUrl },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-profile-view"] });
      showSuccess(
        "Logo Updated",
        "Practice logo has been updated successfully."
      );
    },
    onError: (error) => {
      console.error("Error updating logo:", error);
      showError(
        "Update Failed",
        "Failed to update practice logo. Please try again."
      );
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: async (name: string) => {
      await practiceProfileApi.practiceControllerUpdatePractice({
        updatePracticeDto: { name },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-profile-view"] });
      showSuccess(
        "Name Updated",
        "Practice name has been updated successfully."
      );
    },
    onError: (error) => {
      console.error("Error updating name:", error);
      showError(
        "Update Failed",
        "Failed to update practice name. Please try again."
      );
    },
  });

  const addBranchMutation = useMutation({
    mutationFn: async (branchData: CreatePracticeBranchDto) => {
      await practiceProfileApi.practiceControllerCreateBranch({
        createPracticeBranchDto: branchData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-profile-view"] });
      showSuccess("Branch Added", "New branch has been added successfully.");
    },
    onError: (error) => {
      console.error("Error adding branch:", error);
      showError("Add Failed", "Failed to add branch. Please try again.");
    },
  });

  const updateBranchMutation = useMutation({
    mutationFn: async ({
      branchId,
      branchData,
    }: {
      branchId: string;
      branchData: UpdatePracticeBranchDto;
    }) => {
      await practiceProfileApi.practiceControllerUpdateBranch({
        branchId,
        updatePracticeBranchDto: branchData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-profile-view"] });
      showSuccess("Branch Updated", "Branch has been updated successfully.");
    },
    onError: (error) => {
      console.error("Error updating branch:", error);
      showError("Update Failed", "Failed to update branch. Please try again.");
    },
  });

  const deleteBranchMutation = useMutation({
    mutationFn: async (branchId: string) => {
      await practiceProfileApi.practiceControllerDeleteBranch({ branchId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practice-profile-view"] });
      showSuccess("Branch Deleted", "Branch has been deleted successfully.");
      setDeleteBranchModalOpen(false);
      setSelectedBranch(null);
    },
    onError: (error) => {
      console.error("Error deleting branch:", error);
      showError("Delete Failed", "Failed to delete branch. Please try again.");
    },
  });

  // ========== Modal Handlers ==========

  const handleUpdateBranchClick = useCallback((branch: BranchDto) => {
    setSelectedBranch(branch);
    setUpdateBranchModalOpen(true);
  }, []);

  const handleDeleteBranchClick = useCallback((branch: BranchDto) => {
    setSelectedBranch(branch);
    setDeleteBranchModalOpen(true);
  }, []);

  const handleStaffCountClick = useCallback((branch: BranchDto) => {
    setSelectedBranchForStaff(branch);
    setStaffModalOpen(true);
  }, []);

  const handleUpdateBranch = useCallback(
    async (branchId: string, branchData: UpdatePracticeBranchDto) => {
      await updateBranchMutation.mutateAsync({ branchId, branchData });
      setUpdateBranchModalOpen(false);
      setSelectedBranch(null);
    },
    [updateBranchMutation]
  );

  const handleDeleteBranch = useCallback(async () => {
    if (!selectedBranch?.id) return;
    await deleteBranchMutation.mutateAsync(selectedBranch.id);
  }, [selectedBranch?.id, deleteBranchMutation]);

  if (isLoading) {
    return (
      <DashboardLayout title="Practice Profile">
        <Box className="flex justify-center items-center h-64">
          <CircularProgress size={48} />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Practice Profile">
      <PatientDetailPageContainer
        breadcrumbs={[
          { label: "Dashboard", href: EPath.PROVIDER_DASHBOARD, order: 0 },
        ]}
        lastBreadcrumbItem="Practice Profile"
        title=""
        onBack={handleBack}
        onRefresh={handleRefresh}
        showBackButton={true}
        showRefreshButton={true}
        containerSx={{ height: "calc(100vh - 200px)" }}
        contentSx={{ border: "none", p: 0 }}
      >
        <Box className="space-y-8 p-6">
          <PracticeViewDetails
            practice={profile}
            onUpdateLogo={() => setLogoModalOpen(true)}
            onUpdateName={() => setNameModalOpen(true)}
          />

          <PracticeBranchesView
            branches={profile.branches}
            onUpdateBranch={handleUpdateBranchClick}
            onDeleteBranch={handleDeleteBranchClick}
            onStaffCountClick={handleStaffCountClick}
            onAddBranch={() => setAddBranchModalOpen(true)}
          />
        </Box>
      </PatientDetailPageContainer>

      {/* Modals */}
      <UpdateLogoModal
        open={logoModalOpen}
        currentLogo={profile.logo || profile.profilePicture}
        practiceName={profile.practiceName}
        onClose={() => setLogoModalOpen(false)}
        onUpdate={async (file: File) => {
          await updateLogoMutation.mutateAsync(file);
        }}
      />

      <UpdateNameModal
        open={nameModalOpen}
        currentName={profile.practiceName}
        onClose={() => setNameModalOpen(false)}
        onUpdate={updateNameMutation.mutateAsync}
      />

      <AddBranchModal
        open={addBranchModalOpen}
        onClose={() => setAddBranchModalOpen(false)}
        onAdd={addBranchMutation.mutateAsync}
      />

      <UpdateBranchModal
        open={updateBranchModalOpen}
        branch={selectedBranch}
        onClose={() => {
          setUpdateBranchModalOpen(false);
          setSelectedBranch(null);
        }}
        onUpdate={handleUpdateBranch}
      />

      <DeleteBranchModal
        open={deleteBranchModalOpen}
        branchAddress={selectedBranch?.address || ""}
        staffCount={selectedBranch?.staffCount || 0}
        totalBranches={profile.branches.length}
        isDeleting={deleteBranchMutation.isPending}
        onClose={() => {
          setDeleteBranchModalOpen(false);
          setSelectedBranch(null);
        }}
        onDelete={handleDeleteBranch}
      />

      <StaffMembersModal
        open={staffModalOpen}
        onClose={() => {
          setStaffModalOpen(false);
          setSelectedBranchForStaff(null);
        }}
        branchId={selectedBranchForStaff?.id || ""}
        branchAddress={selectedBranchForStaff?.address || ""}
      />
    </DashboardLayout>
  );
}

export default PracticeProfilePage;
